import styled from '@emotion/styled';
import { Tooltip } from '@mui/material';
import { useAppSelector } from '@/redux/hooks';
import { OVERLAYS_PREVIEW_LABELS_ZINDEX, OVERLAYS_PREVIEW_ZINDEX } from '@/components/Packaging/constants';
import { selectTimelineOverlaysForCurrentFrame } from '@/redux/selectors/packagingTimelineSelectors';
import { isOverlayWithAssignedImage } from '@/components/Packaging/typeGuards';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { OVERLAY_KIND } from '@/redux/slices/packagingSlice.types';

const OverlaysPreview = () => {
  const currentFrameOverlays = useAppSelector(selectTimelineOverlaysForCurrentFrame);
  return (
    <Wrapper>
      {currentFrameOverlays.map((overlay) => {
        const isLowerThird = overlay.kind === OVERLAY_KIND.LOWER_THIRD;

        if (isOverlayWithAssignedImage(overlay)) {
          if (typeof overlay.source.previewUrl === 'string') {
            return <ImageAssignedOverlayPreview key={overlay.draggableId} src={overlay.source.previewUrl} />;
          } else if (overlay.source.previewLoadingError?.reason) {
            return (
              <GeneralOverlayIndicator key={overlay.draggableId}>
                {overlay.label}
                <Tooltip title={`Failed to load preview: ${overlay.source.previewLoadingError.reason}`}>
                  <PolarisIcon icon="error" width={20} height={20} style={{ marginLeft: 8, verticalAlign: -5 }} />
                </Tooltip>
              </GeneralOverlayIndicator>
            );
          }
        }

        if (isLowerThird) {
          // This condition handles only lower-third videos.
          // Lower-third images are handler above, and they rendered full-screen, the same as overlays of other kinds with images assigned to them.
          return <LowerThirdOverlayIndicator key={overlay.draggableId}>{overlay.label}</LowerThirdOverlayIndicator>;
        }

        return <GeneralOverlayIndicator key={overlay.draggableId}>{overlay.label}</GeneralOverlayIndicator>;
      })}
    </Wrapper>
  );
};

export default OverlaysPreview;

const Wrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: ${OVERLAYS_PREVIEW_ZINDEX};
  display: flex;
  flex-direction: column;
  align-items: end;
  padding: ${({ theme }) => theme.spacing(6, 2, 2)};
`;

const ImageAssignedOverlayPreview = styled.img`
  position: absolute;
  z-index: ${OVERLAYS_PREVIEW_ZINDEX};
  top: 0;
  right: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const BaseOverlayIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing(1, 2)};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  background-color: ${({ theme }) => theme.palette.lavender.main + 'D0'};
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  color: ${({ theme }) => (theme.palette.mode === 'dark' ? theme.palette.common.black : theme.palette.common.white)};
  text-align: center;
`;

const GeneralOverlayIndicator = styled(BaseOverlayIndicator)`
  position: relative;
  z-index: ${OVERLAYS_PREVIEW_LABELS_ZINDEX};
  width: 200px;
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

const LowerThirdOverlayIndicator = styled(BaseOverlayIndicator)`
  position: absolute;
  z-index: ${OVERLAYS_PREVIEW_ZINDEX};
  top: 66%;
  left: 7.5%;
  width: 70%;
  height: 25%;
`;
